import type { ScreenBase } from "@frui.ts/screens/dist";
import { ConductorOneChildActive, Router } from "@frui.ts/screens/dist";
import { action, computed, observable } from "mobx";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import ExemptionReportsListViewModel from "./exemptionReports/exemptionReportsListViewModel";
import ExemptionSummaryReportsListViewModel from "./exemptionSummaryReports/exemptionSummaryReportsListViewModel";
import ExpenseReportsListViewModel from "./expenseReports/expenseReportsListViewModel";
import RevenueReportsListViewModel from "./revenueReports/revenueReportsListViewModel";
import BusinessReportsListViewModel from "./businessReports/businessReportsListViewModel";
import InternalReportsCreateViewModel from "./internalReportsCreateViewModel";
import EventBus, { internalReportType } from "services/eventBus";
import InternalReportType from "entities/internalReportType";
import SecurityService from "services/securityService";

const navigationName = "internal-reports";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
  children: [
    ExemptionReportsListViewModel,
    ExemptionSummaryReportsListViewModel,
    ExpenseReportsListViewModel,
    RevenueReportsListViewModel,
  ],
})
export default class InternalReportsViewModel extends ConductorOneChildActive<ScreenBase> {
  @observable addNew = false;
  private unsubscribtions: Array<() => void> = [];

  constructor(
    protected createViewModelFactory: InternalReportsCreateViewModel,
    private localizationService: LocalizationService,
    public securityService: SecurityService,
    private exemptionReportsListViewModel: ExemptionReportsListViewModel,
    private exemptionSummaryReportsListViewModel: ExemptionSummaryReportsListViewModel,
    private expenseReportsListViewModel: ExpenseReportsListViewModel,
    private revenueReportsListViewModel: RevenueReportsListViewModel,
    private businessReportsListViewModel: BusinessReportsListViewModel,
    private eventBus: EventBus
  ) {
    super();
    this.navigationName = navigationName;

    this.children.push(
      this.exemptionReportsListViewModel,
      this.exemptionSummaryReportsListViewModel,
      this.expenseReportsListViewModel,
      this.revenueReportsListViewModel,
      this.businessReportsListViewModel
    );
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscribtions.push(this.eventBus.subscribe(internalReportType, this.onInternalReportChanged));
    this.tryActivateChild(this.children[0]);
    return super.onInitialize();
  }

  @action.bound
  onAddClick() {
    this.createViewModelFactory.setDefaultData(this.activeChild);
    this.createViewModelFactory.addNew = true;
  }

  @action.bound
  onInternalReportChanged(event: ReturnType<typeof internalReportType>) {
    if (event.payload.internalReportType === InternalReportType.Exemptions) {
      this.tryActivateChild(this.exemptionReportsListViewModel);
    }
    if (event.payload.internalReportType === InternalReportType.ExemptionsSummary) {
      this.tryActivateChild(this.exemptionSummaryReportsListViewModel);
    }
    if (event.payload.internalReportType === InternalReportType.Expenses) {
      this.tryActivateChild(this.expenseReportsListViewModel);
    }
    if (event.payload.internalReportType === InternalReportType.Revenues) {
      this.tryActivateChild(this.revenueReportsListViewModel);
    }
  }

  @action.bound
  onClose() {
    this.createViewModelFactory.addNew = false;
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.timeCharges, `internalReports.${code}`, size);
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get canMakeReport() {
    return this.securityService.allowTo("charges.reports", "write");
  }

  @computed
  get createVM() {
    this.createViewModelFactory.vmParent = this.activeChild;
    return this.createViewModelFactory;
  }
}
